// Libs
import React, { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { Modal, Tooltip } from 'antd';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

// Own Libs
import { Button, FormSelect, FormSubmitButton, FormTextArea, MasterDetailForm, Row, Widget } from '@digital/ui';

//Api
import {
  definirVencedorSasPropostaCommand,
  verificarNecessidadeAlteracaoModalidadeContratacao,
  verificarParecerDDIDPCFinanceiro,
  verificarVencedor,
} from '@app/api/sas/sasProposta';

//Files
import {
  ajustarStepOnReloadState,
  dadosGeraisState,
  ordemCompraIdState,
  reloadVendorListState,
  showModalDesobrigarConfideIdState,
  vendorListState,
} from '../atoms';
import { UploadFile, Visibility } from '@app/components';

//Utils
import { mapToOptions } from '@app/helpers/arrayHelper';
import { isObjEmpty, isObjNotEmpty } from '@app/helpers/objHelper';
import { ETipoDemanda } from '@app/enums/ETipoDemanda';

interface Props {}

export const ButtonModalDefinirVencedor = (props: Props) => {
  const ordemCompraId = useRecoilValue(ordemCompraIdState);
  const vendorList = useRecoilValue(vendorListState);
  const dadosGerais = useRecoilValue(dadosGeraisState);

  const [reloadVendorList, setReloadVendorList] = useRecoilState(reloadVendorListState);
  const [showId, setShowId] = useRecoilState(showModalDesobrigarConfideIdState);

  const setAjustarStepOnReload = useSetRecoilState(ajustarStepOnReloadState);

  const [open, setOpen] = useState(false);
  const [parecerFavoravel, setParecerFavoravel] = useState(undefined);
  const [vencedor, setVencedor] = useState('');

  const ehAditivo = dadosGerais.tipo === ETipoDemanda.Aditivo;
  const ehFaturamentoDireto = dadosGerais.tipo === ETipoDemanda.FaturamentoDireto;
  const ehAditivoPcs = dadosGerais.tipoContrato && dadosGerais.tipoContrato.toLowerCase() === 'pcs' && ehAditivo;
  const title = ehAditivo ? 'Confirmar Aditivo' : 'Definir Vencedor';

  const definirVencedor = (values: any) => {
    definirVencedorSasPropostaCommand(values).then((result: any) => {
      if (result && result.valid) {
        {
          onModalOpen();
          if (result.reloadTimeline) {
            window.location.href = window.location.href;
          }
        }
      } else {
        if (result.reloadTimeline) window.location.href = window.location.href;
      }
    });
  };

  const onSubmit = (values: any) => {
    setAjustarStepOnReload(false);
    verificarNecessidadeAlteracaoModalidadeContratacao(values.sasPropostaId).then(
      (necessidadeAlteracaoModalidadeContratacao: any) => {
        if(necessidadeAlteracaoModalidadeContratacao.invalid) {
          return;
        }
        if (necessidadeAlteracaoModalidadeContratacao.solicitarAlteracao) {
          Modal.confirm({
            title: ehAditivoPcs ? 'Alterar Modalidade da Contratação' : 'Alterar Modalidade de Contrato Totvs',
            content: necessidadeAlteracaoModalidadeContratacao.mensagem,
            cancelText: 'Não',
            onCancel: () => {
              values.alterarModalidadeContratacao = false;
              definirVencedor(values);
            },
            okText: 'Sim',
            onOk: () => {
              values.alterarModalidadeContratacao = true;
              definirVencedor(values);
            },
          });
        } else {
          definirVencedor(values);
        }
      },
    );
  };

  useEffect(() => {
    setParecerFavoravel(undefined);

    verificarVencedor(ordemCompraId).then(result => {
      if (result) {
        setVencedor(result.sasPropostaId);
      }
    });
  }, [open, ordemCompraId, reloadVendorList]);

  const onModalOpen = () => {
    setReloadVendorList(!reloadVendorList);
    setOpen(!open);
  };

  const onCancel = () => {
    setReloadVendorList(!reloadVendorList);
    setOpen(false);
  };

  const onSelect = (value: any) => {
    verificarParecerDDIDPCFinanceiro(value).then((data: any) => {
      if (data && data.valid) {
        setParecerFavoravel(data.parecer);
      }
    });
  };

  const widgetModal = useMemo(
    () => (
      <Widget>
        <Row>
          <FormSelect
            name="sasPropostaId"
            options={mapToOptions(vendorList, 'id', 'sasFornecedorOcNome')}
            span={12}
            placeholder="Selecione uma opção"
            onSelect={onSelect}
            style={{ display: 'unset' }}
            defaultValue={vencedor}
          />
        </Row>

        <Visibility visible={isObjNotEmpty(parecerFavoravel) && parecerFavoravel == false}>
          <Row>
            <FormTextArea name="justificativa" label="Justificativa" placeholder="Justificativa" span={12} rows={2} />
          </Row>

          <Visibility visible={!ehFaturamentoDireto}>
            <Row>
              <UploadFile
                nameFormData="arquivo"
                idDoComponente="fileEnvioDdi"
                label="Adicionar Arquivo"
                type="primary"
              />
            </Row>
          </Visibility>
        </Visibility>
      </Widget>
    ),
    [vendorList, vencedor, parecerFavoravel],
  );

  let validationSchemaTmp: any = {
    sasPropostaId: Yup.string().required('Campo obrigatório'),
  };
  if (!ehAditivo && !ehFaturamentoDireto) {
    validationSchemaTmp = {
      ...validationSchemaTmp,
      arquivo: Yup.mixed().required('É necessário anexar um arquivo.'),
    };
  }

  return (
    <>
      <Tooltip title={title}>
        <Button onClick={onModalOpen}>
          {ehAditivo ? 'Confirmar Aditivo' : `${isObjEmpty(vencedor) ? 'Definir' : 'Alterar'} Vencedor`}
        </Button>
      </Tooltip>
      <Visibility visible={open}>
        <MasterDetailForm
          key="formButtonModalDefinirVencedor"
          onSubmit={onSubmit}
          validationSchema={Yup.object().shape(validationSchemaTmp)}>
          <Modal
            onCancel={onCancel}
            title={title}
            visible={open}
            footer={[
              <Button
                title="Fechar"
                onClick={() => {
                  setOpen(false);
                }}
                type="link"
              />,
              <FormSubmitButton noIcon type="primary" title="Salvar" />,
            ]}>
            {widgetModal}
          </Modal>
        </MasterDetailForm>
      </Visibility>
    </>
  );
};
